import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from './components/AppLayout';
import { NotFoundPage } from './components/NotFoundPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PublicOnlyRoute } from './components/PublicOnlyRoute';
import { StatusToasts } from './components/StatusToasts';
import { AdminPage } from './features/admin/AdminPage';
import { AssistantPage } from './features/assistant/AssistantPage';
import { AuthProvider } from './features/auth/AuthProvider';
import { UpgradePage } from './features/billing/UpgradePage';
import { InvitePage } from './features/auth/InvitePage';
import { CalendarPage } from './features/calendar/CalendarPage';
import { LoginPage } from './features/auth/LoginPage';
import { PasswordResetPage } from './features/auth/PasswordResetPage';
import { SignupPage } from './features/auth/SignupPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { GenerationRoute } from './features/generation/GenerationRoute';
import { SharePage } from './features/generation/SharePage';
import { CommunityTemplateStudioPage } from './features/templates/CommunityTemplateStudioPage';
import { MyCommunityTemplatesPage } from './features/templates/MyCommunityTemplatesPage';
import { TemplatesPage } from './features/templates/TemplatesPage';
import { HistoryPage } from './features/history/HistoryPage';
import { KnowledgeBasePage } from './features/knowledge-base/KnowledgeBasePage';
import { LinkedInCallbackPage } from './features/knowledge-base/LinkedInCallbackPage';
import { MetaCallbackPage } from './features/knowledge-base/MetaCallbackPage';
import { NewsPage } from './features/news/NewsPage';
import { AccountProvider } from './features/onboarding/AccountProvider';
import { BrandFromUrl } from './features/onboarding/BrandFromUrl';
import { NewBrandPage, OnboardingPage } from './features/onboarding/OnboardingPage';
import { ReviewPage } from './features/review/ReviewPage';
import { SettingsPage } from './features/settings/SettingsPage';

export function App() {
  return (
    <AuthProvider>
      <AccountProvider>
        <BrowserRouter>
          {/* Above the routes, so sign-in pages get the offline and update notices too. */}
          <StatusToasts />
          {/* `?brand=` from a notification link: switch brand, then drop the param. */}
          <BrandFromUrl />
          <Routes>
            {/* Public */}
            <Route element={<PublicOnlyRoute />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
            </Route>
            {/* Not PublicOnly: the recovery link signs the user in before they
                choose a new password. */}
            <Route path="/reset-password" element={<PasswordResetPage />} />

            {/* Signed in, onboarding not required.
                /admin is here rather than under AppLayout on purpose: an
                operator has no business of their own in play, so the customer
                nav and the quota counter would be describing someone else's
                account. Non-operators get NotFoundPage from the page itself,
                which is what the backend says about the surface too.
                The OAuth callback is here for its own reason: it is a redirect
                target LinkedIn (or Meta) owns the timing of, carrying a single-use code,
                so it must not sit behind a gate that could bounce it. It needs
                a session to post the exchange, and nothing more.
                An invite link is here too: the invitee may have no brand of
                their own, and accepting is what gives them one. */}
            <Route element={<ProtectedRoute />}>
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/integrations/linkedin/callback" element={<LinkedInCallbackPage />} />
              <Route path="/integrations/meta/callback" element={<MetaCallbackPage />} />
            </Route>

            {/* Signed in and onboarded */}
            <Route element={<ProtectedRoute requireOnboarding />}>
              <Route element={<AppLayout />}>
                <Route index element={<DashboardPage />} />
                {/* One more brand on an onboarded account; the shell's brand
                    switcher links here. */}
                <Route path="/brands/new" element={<NewBrandPage />} />
                <Route path="/generation" element={<GenerationRoute />} />
                {/* The installed app's share target: another app's share
                    becomes a brief prefill (vite.config.ts share_target). */}
                <Route path="/share" element={<SharePage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                {/* The community studio. `new` is a static segment, so it
                    outranks the `:templateId` route beside it. */}
                <Route path="/templates/community" element={<MyCommunityTemplatesPage />} />
                <Route path="/templates/community/new" element={<CommunityTemplateStudioPage />} />
                <Route
                  path="/templates/community/:templateId"
                  element={<CommunityTemplateStudioPage />}
                />
                {/* The assistant's full page; the open chat is the path, so it
                    is linkable and survives a reload. */}
                <Route path="/assistant" element={<AssistantPage />} />
                <Route path="/assistant/:threadId" element={<AssistantPage />} />
                {/* The AI Planner folded into the assistant. Old planner chats
                    were copied into threads with no id mapping, so every old
                    link lands on the bare page. */}
                <Route path="/planner" element={<Navigate to="/assistant" replace />} />
                <Route path="/planner/:sessionId" element={<Navigate to="/assistant" replace />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
                <Route path="/review/:requestId" element={<ReviewPage />} />
                <Route path="/history" element={<HistoryPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/upgrade" element={<UpgradePage />} />
              </Route>
            </Route>

            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </BrowserRouter>
      </AccountProvider>
    </AuthProvider>
  );
}
